import {
  addMessageListener,
  removeMessageListener,
  sendMessage,
} from "../../iframe-bridge/messaging/transport";
/** The options to create a typed channel between two frames. */
interface ChannelOptions {
  /** The name of the channel. */
  name: string;
  /** The origin of the publisher. */
  publisherOrigin: string;
  /** The origin of the subscriber. */
  subscriberOrigin: string;
}

interface TypedData<T> {
  name: string;
  payload: T;
}

type Unsubscriber = () => void;

type Publisher<T> = (payload: T) => void;
type Subscriber<T> = (onReceive: (payload: T) => void) => Unsubscriber;

type PublisherFactory<T> = (subscriberWindow: Window) => Publisher<T>;
type SubscriberFactory<T> = (subscriberWindow?: Window) => Subscriber<T>;

/**
 * Creates a publisher / subscriber typed channel via post-message (over iframe).
 */
export const createChannel = <T>({
  name,
  publisherOrigin,
}: ChannelOptions): [PublisherFactory<T>, SubscriberFactory<T>] => {
  /**
   * Creates a publish function to send the typed payload.
   * @param subscriberWindow If publisher is inside the frame, this should be `parent.window`. If outside the frame, should be `frameElement.contentWindow`.
   */
  const createPublisher = (subscriberWindow: Window): Publisher<T> => (
    payload: T
  ) => {
    if (payload === undefined) {
      throw new Error("Published channel data with undefined payload.");
    }
    const data: TypedData<T> = { name, payload };
    sendMessage(subscriberWindow, data);
  };

  /**
   * Creates a subscriber function to receive typed payloads.
   */
  const createSubscriber = (): Subscriber<T> => (
    onReceive: (payload: T) => void
  ) => {
    const receiveMessage = (event: any) => {
      const { origin, data } = event;
      if (publisherOrigin !== "*" && origin !== publisherOrigin) {
        return;
      }

      // If it doesn't contain the name, don't try to deserialize.
      if (data.name !== name) {
        return;
      }

      const typedData = data as TypedData<T>;

      if (!typedData.payload) {
        throw new Error("Received channel data contains no payload.");
      }
      onReceive(typedData.payload);
    };

    addMessageListener(receiveMessage);

    return () => {
      removeMessageListener(receiveMessage);
    };
  };

  return [createPublisher, createSubscriber];
};
